import * as React from 'react';

import {Field, FieldArray} from 'redux-form';
// @ts-expect-error
import type {FieldArrayProps} from 'redux-form';

import {Button} from 'shells/button/button';
import {Line} from 'shells/divider/line';
import {AddAnotherButton} from 'shells/form/sidebar-fields/add-another-button';
import {SelectField} from 'shells/form/sidebar-fields/select-field';
import {nonNull} from 'shells/form/validation/non-null';
import {StackLegacy} from 'shells/layout/stack/stack-legacy';
import {Body} from 'shells/typography/body';

import type {EditionFragment as Edition} from 'nutshell-graphql-types';

import {DripSequenceEditionsQuery} from '../../../graphql-wrappers/drip-sequence-edition/drip-sequence-editions-query';

import {mapEditionLinksToDripSequenceDropdown} from './map-edition-links-to-drip-sequence-dropdown';

import styles from '../forms.module.css';

type Props = {
    dripSequenceId: string;
};

type ComponentProps = FieldArrayProps & {
    editions: Edition[];
};

type OwnProps = FieldArrayProps & {
    dripSequenceId: string;
};

export function LinkClickedForm(props: Props) {
    return (
        <StackLegacy spacing={8}>
            <Body>Which link?</Body>
            <FieldArray
                name='linkClicked'
                component={RenderLinks}
                dripSequenceId={props.dripSequenceId}
            />
        </StackLegacy>
    );
}

function RenderLinksComponent(props: ComponentProps) {
    const {fields, editions} = props;

    const links = editions.length > 0 ? mapEditionLinksToDripSequenceDropdown(editions) : undefined;

    const getLinkOptions = () => {
        const anyLinkOption = [{label: 'Any link', value: '*'}];

        const uniqueLinkOptions = links
            ? links.map((link) => {
                  return {
                      label: getTwoLineOption(link.value, link.secondLine),
                      value: link.value,
                  };
              })
            : undefined;

        // @ts-expect-error
        return uniqueLinkOptions ? anyLinkOption.concat(uniqueLinkOptions) : [];
    };

    const getOptionFields = () => {
        const anyOptionField = [{id: '*', name: 'Any link in this drip sequence'}];

        const uniqueOptionFields = links
            ? links.map((link) => {
                  return {id: link.value, name: link.value};
              })
            : undefined;

        return uniqueOptionFields ? anyOptionField.concat(uniqueOptionFields) : [];
    };

    const noLinksSelected = !fields.get(fields.length - 1);
    const anyLinkIsSelected = fields.getAll()
        ? // @ts-expect-error
          fields.getAll().filter((field) => field === '*').length
        : false;
    const shouldShowAddAnotherButton = !noLinksSelected && !anyLinkIsSelected;

    if (fields.length === 0) {
        // If we don't have any pipelines, add an empty value
        fields.push(null);
    }

    return (
        <StackLegacy spacing={8}>
            {/* @ts-expect-error */}
            {fields.map((member, index) => (
                <div key={member}>
                    <Field
                        name={member}
                        component={SelectField}
                        onRemove={() => {
                            fields.remove(index);
                        }}
                        noOptionsMessage={() => (
                            <div className={styles['empty-option']}>
                                <Body>This drip sequence doesn’t have any links!</Body>
                                <div>When links are added to messages, they’ll show up here</div>
                            </div>
                        )}
                        options={getLinkOptions()}
                        optionFields={getOptionFields()}
                        placeholder='Select a link'
                        validate={[nonNull]}
                    />
                    {index !== fields.length - 1 ? <Line text='or' /> : undefined}
                </div>
            ))}
            {!shouldShowAddAnotherButton ? undefined : (
                <AddAnotherButton
                    text='+ Add another link'
                    onClick={() => {
                        fields.push(null);
                    }}
                />
            )}
            {
                // If there is already at least one selected field but
                // they haven't added the next one yet
                !fields.get(fields.length - 1) && fields.length > 1 ? (
                    <div className={styles['cancel-button']}>
                        <Button onClick={() => fields.remove(-1)}>Cancel</Button>
                    </div>
                ) : null
            }
        </StackLegacy>
    );
}

function RenderLinks(props: OwnProps) {
    return (
        <DripSequenceEditionsQuery dripSequenceId={props.dripSequenceId}>
            {({editions}) => <RenderLinksComponent {...props} editions={editions} />}
        </DripSequenceEditionsQuery>
    );
}

function getTwoLineOption(title: string, secondLine: any) {
    let subtitle = secondLine;

    if (typeof secondLine === 'number') {
        subtitle = `Appears in ${secondLine} messages`;
    }

    return (
        <div>
            <div className={styles['option-title']}>{title}</div>
            <div className={styles['option-subtitle']}>{subtitle}</div>
        </div>
    );
}
